import { AuthRefreshResponse, AuthRefreshData } from '@/types/account'
import { RequestConfig, ApiCredentials } from '@/types/api'
import {
  AUTH_TOKEN_KEY,
  AUTH_TOKEN_EXPIRY_KEY,
  AUTH_REFRESH_TOKEN_KEY,
  AUTH_REFRESH_TOKEN_EXPIRY_KEY,
  TOKEN_REFRESH_BUFFER_MS,
  REFRESH_RETRY_MAX_ATTEMPTS,
  REFRESH_RETRY_BASE_DELAY_MS,
  REFRESH_RETRY_MAX_DELAY_MS,
} from '@/constants/auth'

/**
 * Custom error class for API errors with status code
 */
export class ApiError extends Error {
  status?: number
  code?: string

  constructor(message: string, status?: number, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

/**
 * Sleep utility for retry delays
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Calculate exponential backoff delay with jitter
 */
function calculateRetryDelay(attempt: number): number {
  // Exponential backoff: 1s, 2s, 4s, 8s
  const exponentialDelay = REFRESH_RETRY_BASE_DELAY_MS * Math.pow(2, attempt)
  // Add jitter (±25%) to prevent thundering herd
  const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1)
  const delay = Math.min(exponentialDelay + jitter, REFRESH_RETRY_MAX_DELAY_MS)
  return Math.max(delay, 0)
}

/**
 * Base API Service Class
 *
 * Provides common functionality for all API services including:
 * - Consistent error handling (throws on errors)
 * - Base URL configuration
 * - Authentication token management (access token + refresh token)
 * - Automatic token refresh with exponential backoff retry
 * - Request/response standardization
 */
export abstract class BaseApiService {
  protected baseUrl: string
  protected defaultTimeout: number = 10000
  protected tokenKey: string
  protected tokenExpiryKey: string
  protected refreshTokenKey: string
  protected refreshTokenExpiryKey: string
  private static onLogoutCallback: (() => void) | null = null
  private isRefreshing = false
  private refreshPromise: Promise<boolean> | null = null

  constructor(
    baseUrl?: string,
    tokenKey?: string,
    tokenExpiryKey?: string,
    refreshTokenKey?: string,
    refreshTokenExpiryKey?: string
  ) {
    this.tokenKey = tokenKey || AUTH_TOKEN_KEY
    this.tokenExpiryKey = tokenExpiryKey || AUTH_TOKEN_EXPIRY_KEY
    this.refreshTokenKey = refreshTokenKey || AUTH_REFRESH_TOKEN_KEY
    this.refreshTokenExpiryKey = refreshTokenExpiryKey || AUTH_REFRESH_TOKEN_EXPIRY_KEY

    // Use explicit constructor arg, then build-time Vite env var
    // For runtime configuration, pass baseUrl from route loader data:
    // const { apiBaseUrl } = Route.useLoaderData()
    // const service = new MyService(apiBaseUrl)
    this.baseUrl = baseUrl || import.meta.env.VITE_API_BASE_URL
  }

  /**
   * Register a callback to be invoked when the user should be logged out (e.g., on 401 Unauthorized)
   */
  public static setLogoutHandler(callback: (() => void) | null): void {
    BaseApiService.onLogoutCallback = callback
  }

  /**
   * Get stored authentication credentials
   */
  protected getCredentials(): ApiCredentials | null {
    try {
      const token = localStorage.getItem(this.tokenKey)
      const refreshToken = localStorage.getItem(this.refreshTokenKey)
      const expiresAt = localStorage.getItem(this.tokenExpiryKey)
      const refreshTokenExpiresAt = localStorage.getItem(this.refreshTokenExpiryKey)

      if (!token || !refreshToken || !expiresAt) {
        console.log('[Auth] getCredentials: Missing credentials', {
          hasToken: !!token,
          hasRefreshToken: !!refreshToken,
          hasExpiresAt: !!expiresAt,
          hasRefreshExpiresAt: !!refreshTokenExpiresAt,
        })
        return null
      }

      const result = {
        token,
        refreshToken,
        expiresAt,
        refreshTokenExpiresAt: refreshTokenExpiresAt || undefined,
      }

      console.log('[Auth] getCredentials: Retrieved credentials', {
        tokenExpiry: expiresAt,
        refreshTokenExpiry: refreshTokenExpiresAt || 'not set',
        tokenPreview: token.substring(0, 20) + '...',
        refreshTokenPreview: refreshToken.substring(0, 20) + '...',
      })

      return result
    } catch (error) {
      console.error('[Auth] getCredentials: Failed to retrieve credentials from localStorage:', error)
      return null
    }
  }

  /**
   * Check if the authentication token needs to be refreshed
   */
  protected needsTokenRefresh(): boolean {
    const timeUntil = this.getTimeUntilRefresh()
    const needsRefresh = timeUntil <= 0
    console.log('[Auth] needsTokenRefresh:', {
      needsRefresh,
      timeUntilRefreshMs: timeUntil,
      timeUntilRefreshMin: Math.round(timeUntil / 1000 / 60),
    })
    return needsRefresh
  }

  /**
   * Get the time in milliseconds until the token needs to be refreshed.
   * Returns 0 or negative if refresh is needed now.
   * Returns Infinity if no token/expiry exists.
   */
  public getTimeUntilRefresh(): number {
    const credentials = this.getCredentials()

    if (!credentials?.expiresAt) {
      console.log('[Auth] getTimeUntilRefresh: No token expiry found, returning Infinity')
      return Infinity
    }

    try {
      const expiresAt = new Date(credentials.expiresAt)
      const now = new Date()
      const timeUntil = expiresAt.getTime() - now.getTime() - TOKEN_REFRESH_BUFFER_MS
      console.log('[Auth] getTimeUntilRefresh:', {
        tokenExpiresAt: credentials.expiresAt,
        now: now.toISOString(),
        timeUntilRefreshMs: timeUntil,
        timeUntilRefreshMin: Math.round(timeUntil / 1000 / 60),
        bufferMs: TOKEN_REFRESH_BUFFER_MS,
      })
      return timeUntil
    } catch (error) {
      console.error('[Auth] getTimeUntilRefresh: Failed to parse token expiry date:', error)
      return 0
    }
  }

  /**
   * Check if a valid token exists (not expired)
   */
  public hasValidToken(): boolean {
    const credentials = this.getCredentials()
    if (!credentials?.token || !credentials?.expiresAt) {
      console.log('[Auth] hasValidToken: false - missing token or expiry')
      return false
    }

    try {
      const expiresAt = new Date(credentials.expiresAt)
      const now = new Date()
      const isValid = expiresAt > now
      console.log('[Auth] hasValidToken:', {
        isValid,
        tokenExpiresAt: credentials.expiresAt,
        now: now.toISOString(),
        minutesUntilExpiry: Math.round((expiresAt.getTime() - now.getTime()) / 1000 / 60),
      })
      return isValid
    } catch {
      console.log('[Auth] hasValidToken: false - failed to parse expiry date')
      return false
    }
  }

  /**
   * Check if refresh token is valid (not expired)
   */
  public hasValidRefreshToken(): boolean {
    const credentials = this.getCredentials()
    if (!credentials?.refreshToken) {
      console.log('[Auth] hasValidRefreshToken: false - no refresh token')
      return false
    }

    // If no expiry is set, assume it's valid
    if (!credentials.refreshTokenExpiresAt) {
      console.log('[Auth] hasValidRefreshToken: true - no expiry set (assumed valid)')
      return true
    }

    try {
      const expiresAt = new Date(credentials.refreshTokenExpiresAt)
      const now = new Date()
      const isValid = expiresAt > now
      console.log('[Auth] hasValidRefreshToken:', {
        isValid,
        refreshTokenExpiresAt: credentials.refreshTokenExpiresAt,
        now: now.toISOString(),
        minutesUntilExpiry: Math.round((expiresAt.getTime() - now.getTime()) / 1000 / 60),
      })
      return isValid
    } catch {
      console.log('[Auth] hasValidRefreshToken: false - failed to parse expiry date')
      return false
    }
  }

  /**
   * Store authentication credentials (access token + refresh token)
   */
  protected setCredentials(credentials: ApiCredentials): void {
    try {
      if (credentials.token) {
        localStorage.setItem(this.tokenKey, credentials.token)
      }
      if (credentials.expiresAt) {
        localStorage.setItem(this.tokenExpiryKey, credentials.expiresAt)
      }
      if (credentials.refreshToken) {
        localStorage.setItem(this.refreshTokenKey, credentials.refreshToken)
      }
      if (credentials.refreshTokenExpiresAt) {
        localStorage.setItem(this.refreshTokenExpiryKey, credentials.refreshTokenExpiresAt)
      }

      console.log('[Auth] setCredentials: Stored new credentials', {
        hasToken: !!credentials.token,
        hasRefreshToken: !!credentials.refreshToken,
        tokenExpiresAt: credentials.expiresAt,
        refreshTokenExpiresAt: credentials.refreshTokenExpiresAt,
        tokenPreview: credentials.token ? credentials.token.substring(0, 20) + '...' : null,
        refreshTokenPreview: credentials.refreshToken ? credentials.refreshToken.substring(0, 20) + '...' : null,
      })
    } catch (error) {
      console.error('[Auth] setCredentials: Failed to store credentials in localStorage:', error)
    }
  }

  /**
   * Clear stored authentication credentials
   */
  protected clearCredentials(): void {
    try {
      console.log('[Auth] clearCredentials: Clearing all auth credentials')
      localStorage.removeItem(this.tokenKey)
      localStorage.removeItem(this.tokenExpiryKey)
      localStorage.removeItem(this.refreshTokenKey)
      localStorage.removeItem(this.refreshTokenExpiryKey)
    } catch (error) {
      console.error('[Auth] clearCredentials: Failed to clear credentials from localStorage:', error)
    }
  }

  /**
   * Perform a single refresh token attempt
   * Uses the refresh token in the Authorization header
   */
  private async performRefreshAttempt(): Promise<AuthRefreshData> {
    const credentials = this.getCredentials()

    if (!credentials?.refreshToken) {
      console.error('[Auth] performRefreshAttempt: No refresh token available')
      throw new ApiError('No refresh token available', 401, 'NO_REFRESH_TOKEN')
    }

    const url = `${this.baseUrl}/auth/refresh`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${credentials.refreshToken}`,
    }

    console.log('[Auth] performRefreshAttempt: Making refresh request', {
      url,
      refreshTokenPreview: credentials.refreshToken.substring(0, 20) + '...',
    })

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.defaultTimeout)

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`
        try {
          const errorBody = await response.json()
          errorMessage = errorBody.message || errorBody.error || errorMessage
        } catch {
          errorMessage = response.statusText || errorMessage
        }
        console.error('[Auth] performRefreshAttempt: Refresh request failed', {
          status: response.status,
          errorMessage,
        })
        throw new ApiError(errorMessage, response.status, `HTTP_${response.status}`)
      }

      const json = await response.json() as AuthRefreshResponse
      console.log('[Auth] performRefreshAttempt: Refresh successful', {
        hasToken: !!json.data.token,
        hasRefreshToken: !!json.data.refreshToken,
        tokenExpiresAt: json.data.expiresAt,
      })
      return json.data
    } catch (error) {
      clearTimeout(timeoutId)
      throw error
    }
  }

  /**
   * Refresh the authentication token with exponential backoff retry
   *
   * - Uses refreshToken for authentication
   * - Implements sliding refresh tokens (new refresh token on each refresh)
   * - Retries on transient errors (network issues, 5xx responses)
   * - Immediately fails and clears credentials on 401/403
   * - Maximum of 4 retry attempts with exponential backoff
   *
   * @returns true if refresh was successful, false otherwise
   */
  public async refreshToken(): Promise<boolean> {
    console.log('[Auth] refreshToken: Starting token refresh process', {
      isAlreadyRefreshing: this.isRefreshing,
    })

    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      console.log('[Auth] refreshToken: Returning existing refresh promise')
      return this.refreshPromise
    }

    // Check if we have a valid refresh token
    const hasValidRefresh = this.hasValidRefreshToken()
    console.log('[Auth] refreshToken: Validating refresh token', {
      hasValidRefreshToken: hasValidRefresh,
    })

    if (!hasValidRefresh) {
      console.error('[Auth] refreshToken: No valid refresh token, clearing credentials and logging out')
      this.clearCredentials()
      BaseApiService.onLogoutCallback?.()
      return false
    }

    this.isRefreshing = true

    // Create the refresh promise
    this.refreshPromise = this.executeRefreshWithRetry()

    try {
      const result = await this.refreshPromise
      console.log('[Auth] refreshToken: Refresh completed with result:', result)
      return result
    } finally {
      this.isRefreshing = false
      this.refreshPromise = null
    }
  }

  /**
   * Execute refresh with exponential backoff retry logic
   */
  private async executeRefreshWithRetry(): Promise<boolean> {
    let lastError: Error | null = null

    console.log('[Auth] executeRefreshWithRetry: Starting refresh with retry', {
      maxAttempts: REFRESH_RETRY_MAX_ATTEMPTS,
    })

    for (let attempt = 0; attempt <= REFRESH_RETRY_MAX_ATTEMPTS; attempt++) {
      try {
        console.log('[Auth] executeRefreshWithRetry: Attempt', {
          attempt: attempt + 1,
          maxAttempts: REFRESH_RETRY_MAX_ATTEMPTS + 1,
        })

        if (attempt > 0) {
          const delay = calculateRetryDelay(attempt - 1)
          console.log('[Auth] executeRefreshWithRetry: Waiting before retry', { delayMs: Math.round(delay) })
          await sleep(delay)
        }

        const result = await this.performRefreshAttempt()

        if (!result.token || !result.expiresAt) {
          console.error('[Auth] executeRefreshWithRetry: Invalid refresh response - missing token or expiry')
          throw new ApiError('Invalid refresh response', 500, 'INVALID_RESPONSE')
        }

        console.log('[Auth] executeRefreshWithRetry: Refresh successful, storing new credentials')
        this.setCredentials({
          token: result.token,
          refreshToken: result.refreshToken,
          expiresAt: result.expiresAt,
          refreshTokenExpiresAt: result.refreshTokenExpiresAt,
        })

        console.log('[Auth] executeRefreshWithRetry: Refresh completed successfully')
        return true

      } catch (error) {
        lastError = error as Error
        const apiError = error as ApiError
        const status = apiError.status

        console.error('[Auth] executeRefreshWithRetry: Refresh attempt failed', {
          attempt: attempt + 1,
          status,
          errorCode: apiError.code,
          errorMessage: apiError.message,
        })

        // 401/403: Permanent failure - don't retry, clear credentials and logout
        if (status === 401 || status === 403) {
          console.error('[Auth] executeRefreshWithRetry: Permanent failure (401/403), clearing credentials and logging out')
          this.clearCredentials()
          BaseApiService.onLogoutCallback?.()
          return false
        }

        if (attempt < REFRESH_RETRY_MAX_ATTEMPTS) {
          console.warn(`[Auth] Token refresh attempt ${attempt + 1} failed, will retry`, {
            error: apiError.message,
            status: apiError.status,
          })
        }
      }
    }

    // All retries exhausted
    console.error('[Auth] Token refresh failed after max attempts', {
      totalAttempts: REFRESH_RETRY_MAX_ATTEMPTS + 1,
      lastError: lastError?.message,
      lastErrorCode: (lastError as ApiError)?.code,
    })
    this.clearCredentials()
    BaseApiService.onLogoutCallback?.()
    return false
  }

  /**
   * Build request headers including authentication if needed.
   * Does not trigger logout — missing credentials simply omit the auth header;
   * the server will respond with 401 which makeRequest handles.
   */
  protected buildHeaders(config: RequestConfig = {}): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...config.headers,
    }

    if (config.requiresAuth) {
      const credentials = this.getCredentials()
      if (credentials?.token) {
        headers['Authorization'] = `Bearer ${credentials.token}`
      }
    }

    return headers
  }

  /**
   * Make HTTP request - throws on errors for React Query compatibility.
   * On 401, automatically attempts a token refresh and retries once before logging out.
   */
  protected async makeRequest<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    return this._makeRequest<T>(endpoint, config, false)
  }

  private async _makeRequest<T>(endpoint: string, config: RequestConfig, isRetry: boolean): Promise<T> {
    const {
      method = 'GET',
      body,
      timeout = this.defaultTimeout,
      ...restConfig
    } = config

    console.log('[Auth] _makeRequest: Starting request', {
      endpoint,
      method,
      requiresAuth: restConfig.requiresAuth,
      isRetry,
    })

    // Proactively refresh token if it's due, before making the request
    if (restConfig.requiresAuth && this.needsTokenRefresh()) {
      console.log('[Auth] _makeRequest: Token needs refresh, attempting refresh before request')
      await this.refreshToken()
    }

    const url = `${this.baseUrl}${endpoint}`
    const headers = this.buildHeaders(restConfig)

    console.log('[Auth] _makeRequest: Request headers', {
      hasAuthHeader: !!headers['Authorization'],
      authHeaderPreview: headers['Authorization'] ? headers['Authorization'].substring(0, 30) + '...' : 'none',
    })

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    const fetchOptions: RequestInit = {
      method,
      headers,
      signal: controller.signal,
    }

    if (body && method !== 'GET') {
      fetchOptions.body = typeof body === 'string' ? body : JSON.stringify(body)
    }

    let response: Response
    try {
      response = await fetch(url, fetchOptions)
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiError(`Request timeout after ${timeout}ms`, undefined, 'TIMEOUT')
      }
      throw error
    }
    clearTimeout(timeoutId)

    if (!response.ok) {
      console.error('[Auth] _makeRequest: Request failed', {
        endpoint,
        status: response.status,
        statusText: response.statusText,
        isRetry,
      })

      // On 401, try refreshing and retrying once before giving up
      if (response.status === 401 && restConfig.requiresAuth && !isRetry) {
        console.log('[Auth] _makeRequest: Got 401, attempting token refresh and retry')
        const refreshed = await this.refreshToken()
        console.log('[Auth] _makeRequest: Token refresh result for retry', { refreshed })
        if (refreshed) {
          console.log('[Auth] _makeRequest: Retry with new token')
          return this._makeRequest<T>(endpoint, config, true)
        }
        // refreshToken() already cleared credentials and called logout
        console.error('[Auth] _makeRequest: Token refresh failed during retry, session expired')
        throw new ApiError('Session expired', 401, 'UNAUTHORIZED')
      }

      // On 401 (retry failed) or 403, force logout
      if (response.status === 401 || response.status === 403) {
        console.error('[Auth] _makeRequest: 401/403 response, forcing logout', {
          status: response.status,
          isRetry,
        })
        this.clearCredentials()
        BaseApiService.onLogoutCallback?.()
      }

      let errorMessage = `HTTP ${response.status}`
      try {
        const errorBody = await response.json()
        errorMessage = errorBody.message || errorBody.error || errorMessage
      } catch {
        errorMessage = response.statusText || errorMessage
      }

      throw new ApiError(errorMessage, response.status, `HTTP_${response.status}`)
    }

    console.log('[Auth] _makeRequest: Request successful', { endpoint, status: response.status })

    const contentType = response.headers.get('content-type')

    if (contentType?.includes('application/json')) {
      const json = await response.json()
      if (json && typeof json === 'object' && 'data' in json) {
        return json.data as T
      }
      return json as T
    }

    return (await response.text()) as unknown as T
  }

  /**
   * Convenience methods for common HTTP verbs
   */
  protected async get<T>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'GET' })
  }

  protected async post<T>(endpoint: string, body?: unknown, config?: Omit<RequestConfig, 'method'>): Promise<T> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'POST', body })
  }

  protected async put<T>(endpoint: string, body?: unknown, config?: Omit<RequestConfig, 'method'>): Promise<T> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'PUT', body })
  }

  protected async delete<T>(endpoint: string, config?: Omit<RequestConfig, 'method' | 'body'>): Promise<T> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'DELETE' })
  }

  protected async patch<T>(endpoint: string, body?: unknown, config?: Omit<RequestConfig, 'method'>): Promise<T> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'PATCH', body })
  }
}
